import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Truck, Check, Clock, MapPin, ChevronRight, Search } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import Reveal from "@/components/Reveal";

const STATUS_STEPS = [
  { key: "pending", label: "Order placed", icon: Check },
  { key: "processing", label: "Preparing your piece", icon: Package },
  { key: "shipped", label: "On the way", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Check },
];

const TrackOrder = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const initialQuery = orderId || params.get("order") || "";
  const [query, setQuery] = useState(initialQuery);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTrack = async (q) => {
    if (!q) return;
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/orders/${encodeURIComponent(q)}/track`);
      setData(r.data);
    } catch (err) {
      setError(err?.response?.status === 404 ? "We couldn't find that order. Please check the number and try again." : "Could not look up that order right now.");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (initialQuery) fetchTrack(initialQuery); /* eslint-disable-next-line */ }, [initialQuery]);

  const currentStepIdx = data ? Math.max(0, STATUS_STEPS.findIndex((s) => s.key === data.order_status)) : 0;

  return (
    <div className="bg-cream min-h-[80vh]" data-testid="track-page">
      <section className="rieave-container pt-28 md:pt-36 pb-10">
        <Reveal>
          <p className="eyebrow mb-3">Tracking</p>
          <h1 className="font-hero text-5xl md:text-6xl leading-[1] mb-3">
            Where's my <span className="serif-italic text-rose">order</span>?
          </h1>
          <p className="text-charcoal/65 max-w-md">
            Enter your order number (e.g. RIE010012) or AWB tracking number to see the latest status.
          </p>
        </Reveal>

        <form
          onSubmit={(e) => { e.preventDefault(); fetchTrack(query); }}
          className="mt-8 flex max-w-lg gap-2"
          data-testid="track-form"
        >
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mauve" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Order #RIE… or AWB…"
              className="w-full pl-10 h-12 rounded-full border border-charcoal/15 bg-white px-4 outline-none focus:border-rose font-body text-sm"
              data-testid="track-input"
            />
          </div>
          <button type="submit" className="btn-primary" data-testid="track-submit">
            {loading ? "Looking…" : "Track →"}
          </button>
        </form>

        {error && (
          <p className="text-sm text-rose mt-4 max-w-md" data-testid="track-error">{error}</p>
        )}
      </section>

      <AnimatePresence>
        {data && (
          <motion.section
            key={data.order_number}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            className="rieave-container pb-24"
            data-testid="track-result"
          >
            {/* Order summary card */}
            <div className="bg-white rounded-2xl border border-charcoal/8 p-7 md:p-9 mb-6 shadow-sm">
              <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                  <p className="eyebrow mb-1">Order</p>
                  <h2 className="font-hero text-3xl text-charcoal">{data.order_number}</h2>
                </div>
                <div className="text-right">
                  <p className="eyebrow mb-1">Total</p>
                  <p className="font-hero text-2xl">{formatINR(data.total || 0)}</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="relative pb-2 mb-2 overflow-x-auto">
                <div className="flex items-center justify-between min-w-[480px]">
                  {STATUS_STEPS.map((s, i) => {
                    const Icon = s.icon;
                    const done = i <= currentStepIdx;
                    return (
                      <React.Fragment key={s.key}>
                        <div className="flex flex-col items-center text-center flex-shrink-0 w-24">
                          <motion.div
                            initial={false}
                            animate={{ scale: done ? 1 : 0.92 }}
                            className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                              done ? "bg-rose text-white" : "bg-blush/40 text-mauve"
                            }`}
                            data-testid={`track-step-${s.key}`}
                          >
                            <Icon size={16} />
                          </motion.div>
                          <p className={`text-[11px] uppercase tracking-[0.2em] font-medium ${done ? "text-charcoal" : "text-mauve"}`}>
                            {s.label}
                          </p>
                        </div>
                        {i < STATUS_STEPS.length - 1 && (
                          <div className={`flex-1 h-[2px] mx-1 transition-colors ${i < currentStepIdx ? "bg-rose" : "bg-charcoal/10"}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Tracking number */}
              {data.awb && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-blush/30 border border-rose/20" data-testid="track-awb-block">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-mauve mb-0.5">AWB / tracking #</p>
                    <p className="font-mono text-lg text-charcoal" data-testid="track-awb">{data.awb}</p>
                  </div>
                  <span className="text-xs text-mauve">via {data.courier || "Xpressbees"}</span>
                </div>
              )}
              {!data.awb && data.order_status !== "delivered" && (
                <p className="mt-5 text-sm text-mauve">
                  We're preparing your piece — tracking details will appear here as soon as it ships.
                </p>
              )}
            </div>

            {/* Items + Address */}
            <div className="grid md:grid-cols-2 gap-5 mb-6">
              <div className="bg-white rounded-2xl border border-charcoal/8 p-6">
                <p className="eyebrow mb-4"><Package size={11} className="inline mr-1.5 -mt-0.5" /> Pieces in this order</p>
                <div className="space-y-3">
                  {(data.items || []).map((it, i) => (
                    <div key={i} className="flex gap-3 items-center">
                      {it.image && <img src={it.image} alt={it.name} className="w-12 h-14 object-cover rounded" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-cormorant text-base text-charcoal truncate">{it.name}</p>
                        <p className="text-xs text-mauve">Qty {it.quantity}{it.size ? ` · ${it.size}` : ""}</p>
                      </div>
                      <p className="text-sm">{formatINR((it.price || 0) * (it.quantity || 1))}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-charcoal/8 p-6">
                <p className="eyebrow mb-4"><MapPin size={11} className="inline mr-1.5 -mt-0.5" /> Delivering to</p>
                {data.shipping_address ? (
                  <div className="text-charcoal/75 text-[15px] leading-[1.6]">
                    <p className="font-cormorant text-lg text-charcoal">{data.shipping_address.full_name}</p>
                    <p>{data.shipping_address.line1}{data.shipping_address.line2 ? `, ${data.shipping_address.line2}` : ""}</p>
                    <p>{data.shipping_address.city}, {data.shipping_address.state} {data.shipping_address.postal_code}</p>
                    <p className="text-xs text-mauve mt-1">{data.shipping_address.phone}</p>
                  </div>
                ) : <p className="text-mauve text-sm">—</p>}
              </div>
            </div>

            {/* Timeline */}
            {data.timeline && data.timeline.length > 0 && (
              <div className="bg-white rounded-2xl border border-charcoal/8 p-7" data-testid="track-timeline">
                <p className="eyebrow mb-5"><Clock size={11} className="inline mr-1.5 -mt-0.5" /> Journey so far</p>
                <ol className="relative pl-6 border-l border-rose/30">
                  {data.timeline.map((t, i) => (
                    <li key={i} className="mb-6 last:mb-0">
                      <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-rose ring-4 ring-cream" />
                      <p className="font-cormorant text-base text-charcoal">{t.status_description || t.status || t.message || "Update"}</p>
                      <p className="text-xs text-mauve">
                        {t.location ? t.location + " · " : ""}
                        {t.event_time || t.timestamp || t.date || ""}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="mt-8 text-center">
              <Link to="/contact" className="btn-link">
                Have a question? Reach our team <ChevronRight size={14} />
              </Link>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrackOrder;
